import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import { Cell, Row } from "react-aria-components";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableRow from "components/Table/Row";
import useSelectionStore from "components/Table/useSelectionStore";
import useTableSort from "components/Table/useTableSort";
import TableWrapper from "components/Table/Wrapper";
import EmptyState from "ds/components/EmptyState";
import useErrorHandle from "hooks/useErrorHandle";
import { Role } from "types/generated";

import RolesBulkActions from "./BulkActions";
import {
  ANALYTICS,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  ROLES_COLUMN_CONFIG,
  ROLES_COLUMNS_STORAGE_KEY,
  ROLES_CUSTOM_END_COLUMN_CONFIG,
  ROW_HEADER_ID,
  SORT_CONFIG,
} from "./constants";
import RolesEmpty from "./Empty";
import { GET_ROLES } from "./gql";
import RolesListItem from "./ListItem";
import RolesPageLayout from "./PageLayout";
import { useFilteredEntities } from "./useFilteredEntities";

type RolesProps = {
  readOnly?: boolean;
};

const Roles = ({ readOnly }: RolesProps) => {
  const { resetAllSelected, selected, unselect, resetStore } = useSelectionStore();
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const { data, loading, refetch, error } = usePolledQuery(GET_ROLES, {
    pollingGroup: PollingIntervalGroups.Lists,
  });

  const handleBulkActionsFinish = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const onBulkResetAll = useCallback(() => {
    resetAllSelected();
  }, [resetAllSelected]);

  // reset store when unmounted
  useEffect(() => resetStore, [resetStore]);

  const renderRow = useCallback(
    (
      item: Partial<Role> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return (
          <Row>
            <Cell style={{ height: item.height }} />
          </Row>
        );
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.name}
          isSelectableDisabled={item.isSystem}
          selectTooltip={item.isSystem ? () => "System roles cannot be managed" : undefined}
        >
          <RolesListItem role={item as Role} readOnly={readOnly} />
        </TableRow>
      );
    },
    [readOnly]
  );

  const roles = useMemo(
    () => data?.roles.map((item) => ({ ...item, isSelectionDisabled: item.isSystem })) || [],
    [data?.roles]
  );

  // Exclude system roles
  const nonSelectableIds = useMemo(
    () =>
      roles.reduce(
        (prev, next) => (next.isSelectionDisabled ? [...prev, next.id] : prev),
        [] as string[]
      ),
    [roles]
  );

  const memoizedSelectedRolesMap = useMemo(
    () =>
      new Map(
        data?.roles
          .filter((edge) => !nonSelectableIds.includes(edge.id))
          .map((edge) => [edge.id, edge])
      ),
    [data?.roles, nonSelectableIds]
  );

  const filteredRoles = useFilteredEntities(roles, sortDescriptor);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.roles) {
    return (
      <RolesPageLayout>
        <PageLayoutSkeleton />
      </RolesPageLayout>
    );
  }

  if (!loading && !data?.roles) {
    return <NotFoundPage />;
  }

  return (
    <TableContextProvider
      selectable={!readOnly}
      columnsConfig={ROLES_COLUMN_CONFIG}
      endCustomColumns={ROLES_CUSTOM_END_COLUMN_CONFIG}
      initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
      rowHeaderId={ROW_HEADER_ID}
      localStorageId={ROLES_COLUMNS_STORAGE_KEY}
      setSortDescriptor={handleSortOptionChange}
      sortDescriptor={sortDescriptor}
      {...ANALYTICS}
    >
      <RolesPageLayout readOnly={readOnly}>
        <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
          {!!filteredRoles?.length && (
            <Table
              nonSelectableIds={nonSelectableIds}
              ariaLabel="Roles list"
              items={filteredRoles}
              ref={virtualizedListContainerRef}
            >
              {renderRow}
            </Table>
          )}

          {selected.size > 0 && !readOnly && (
            <RolesBulkActions
              virtualizedListContainerRef={virtualizedListContainerRef}
              selectedSet={selected}
              rolesMap={memoizedSelectedRolesMap}
              onBulkResetAll={onBulkResetAll}
              onItemDismiss={unselect}
              onFinish={handleBulkActionsFinish}
            />
          )}
          {!roles?.length && <RolesEmpty />}
          {!filteredRoles.length && !!roles?.length && (
            <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
          )}
        </TableWrapper>
      </RolesPageLayout>
    </TableContextProvider>
  );
};

export default Roles;
